import { useEffect, useState, RefObject } from 'react';
import { useWindowSize } from '../shared/useWindowSize';

interface Props {
  tableContainerRef: RefObject<HTMLDivElement | null>;
  items: ReadonlyArray<unknown>;
  bottomMargin?: number;
  extraDeps?: ReadonlyArray<unknown>;
}

const DEFAULT_TABLE_HEIGHT = 400;
const DEFAULT_HEADER_HEIGHT = 55;
const DEFAULT_FOOTER_HEIGHT = 90;
const MIN_TABLE_HEIGHT = 200;
const DEFAULT_BOTTOM_MARGIN = 2;
const ANIMATION_DURATION = 350;

const EMPTY_DEPS: ReadonlyArray<unknown> = [];

export const useTableHeight = ({
  tableContainerRef,
  items,
  bottomMargin = DEFAULT_BOTTOM_MARGIN,
  extraDeps = EMPTY_DEPS,
}: Props) => {
  const { height: windowHeight } = useWindowSize();

  const [tableHeight, setTableHeight] = useState<number>(DEFAULT_TABLE_HEIGHT);
  const [containerHeight, setContainerHeight] = useState<number>(
    DEFAULT_TABLE_HEIGHT + DEFAULT_HEADER_HEIGHT,
  );

  useEffect(() => {
    const updateHeight = () => {
      const el = tableContainerRef?.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      const paddingTop = parseFloat(cs.paddingTop) || 0;
      const paddingBottom = parseFloat(cs.paddingBottom) || 0;

      const tableHeaderEl = el.querySelector<HTMLElement>('.ant-table-header');
      const theadEl = el.querySelector<HTMLElement>('.ant-table-thead');
      const headerHeight =
        tableHeaderEl?.offsetHeight ??
        theadEl?.offsetHeight ??
        DEFAULT_HEADER_HEIGHT;

      let footerHeight = 0;
      const children = el.children;
      for (let i = 1; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        if (child) {
          const childCs = getComputedStyle(child);
          footerHeight +=
            child.offsetHeight +
            (parseFloat(childCs.marginTop) || 0) +
            (parseFloat(childCs.marginBottom) || 0);
        }
      }
      if (footerHeight === 0) footerHeight = DEFAULT_FOOTER_HEIGHT;

      const available = windowHeight - rect.top - paddingTop - paddingBottom;
      const scrollY = Math.max(
        MIN_TABLE_HEIGHT,
        available - headerHeight - footerHeight - bottomMargin,
      );

      setTableHeight(scrollY);
      setContainerHeight(scrollY + headerHeight);
    };

    updateHeight();

    const timer = setTimeout(updateHeight, ANIMATION_DURATION);
    const el = tableContainerRef?.current;
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && el
        ? new ResizeObserver(updateHeight)
        : null;

    if (resizeObserver && el) {
      resizeObserver.observe(el);

      if (el.parentElement) {
        resizeObserver.observe(el.parentElement);
      }

      if (el.parentElement?.parentElement) {
        resizeObserver.observe(el.parentElement.parentElement);
      }
    }

    return () => {
      clearTimeout(timer);
      resizeObserver?.disconnect();
    };
  }, [items.length, windowHeight, bottomMargin, ...extraDeps]);

  return { tableHeight, containerHeight, isTabletScroll: true };
};
